/**
 * UseCasesPage - Refactored with outcome-focused cards
 * "Choose your reality" approach
 */

import { Button } from "@/components/ui/button";
import { Check, Target, Clock, TrendingUp } from "lucide-react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/Section";
import { Container } from "@/components/marketing/Container";
import { Split } from "@/components/marketing/Split";
import { FeatureGrid } from "@/components/marketing/FeatureGrid";
import { ConversionCTA } from "@/components/marketing/ConversionCTA";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { redirectToStripeCheckout } from "@/lib/stripe";
import { motion } from "framer-motion";

const useCases = [
  {
    id: "sales-reps",
    icon: Target,
    title: "Sales Reps",
    subtitle: "Stop guessing. Start closing.",
    description: "Wake up every morning knowing exactly who to call, in what order, and why. No more analysis paralysis. Just execute and win.",
    benefits: [
      "3-7 prioritized actions every day",
      "One-click to call, WhatsApp, or email",
      "Clear reasoning for every priority",
      "More calls, better conversations, faster closes"
    ],
    outcomes: [
      { label: "More calls per day", value: "40%", icon: TrendingUp },
      { label: "Faster response time", value: "3x", icon: Clock },
    ],
    quote: "I used to spend 30 minutes every morning figuring out who to call. Now I just open the app and start executing."
  },
  {
    id: "sales-managers",
    icon: Target,
    title: "Sales Managers",
    subtitle: "See what your team is doing—without micromanaging.",
    description: "Get real-time visibility into team performance without drowning in spreadsheets. Know which deals need attention and which reps need coaching.",
    benefits: [
      "Team performance dashboard",
      "Revenue-at-risk alerts",
      "Activity tracking without manual logging",
      "Data-driven coaching conversations"
    ],
    outcomes: [
      { label: "Time saved on reporting", value: "5h/week", icon: Clock },
      { label: "Pipeline visibility", value: "100%", icon: TrendingUp },
    ],
    quote: "Finally, I can see what's happening across my team without asking for status updates every day."
  },
  {
    id: "founders",
    icon: Target,
    title: "Founders",
    subtitle: "Close deals while building your company.",
    description: "You're wearing 10 hats. Sales shouldn't consume your whole day. Get the most important sales actions in 30 minutes—then get back to building.",
    benefits: [
      "Focus on highest-impact opportunities",
      "Never miss a follow-up",
      "Simple workflow, no CRM complexity",
      "Know exactly where your pipeline stands"
    ],
    outcomes: [
      { label: "Time on sales tasks", value: "-60%", icon: Clock },
      { label: "Deal close rate", value: "+25%", icon: TrendingUp },
    ],
    quote: "As a founder, I can't spend 2 hours a day in a CRM. This gives me clarity in 30 minutes."
  },
  {
    id: "revops",
    icon: Target,
    title: "RevOps",
    subtitle: "Finally, a system reps actually use.",
    description: "Stop fighting with sales teams about CRM adoption. Decision Sales OS is built for execution, so reps actually want to use it—and you get clean data.",
    benefits: [
      "High adoption rates",
      "Clean, actionable data",
      "Revenue impact visibility",
      "Integrates with your existing stack"
    ],
    outcomes: [
      { label: "CRM adoption", value: "95%", icon: TrendingUp },
      { label: "Data quality", value: "+80%", icon: TrendingUp },
    ],
    quote: "Adoption went from 40% to 95% in two weeks. The data we're getting now is actually useful."
  }
];

export default function UseCasesPage() {
  return (
    <MarketingLayout showBackLink>
      {/* Hero */}
      <Section spacing="lg">
        <Container size="lg">
          <ScrollReveal>
            <h1 className="text-[var(--font-display)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-display)]">
              Built for Teams That Sell
            </h1>
            <p className="text-[var(--font-body-lg)] text-muted-foreground max-w-2xl leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
              Whether you're a solo founder or a sales team of 50, Decision Sales OS adapts to how you work.
            </p>
          </ScrollReveal>
        </Container>
      </Section>

      {/* Use Cases - Outcome-Focused */}
      <Section spacing="xl">
        <Container size="xl">
          <div className="space-y-32">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <ScrollReveal key={useCase.id}>
                  <Split reverse={index % 2 === 1} gap="xl" align="center">
                    <div>
                      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                        <Icon className="w-4 h-4" />
                        {useCase.title}
                      </div>
                      <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-4 leading-[var(--leading-headline)]">
                        {useCase.subtitle}
                      </h2>
                      <p className="text-[var(--font-body-lg)] text-muted-foreground mb-8 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                        {useCase.description}
                      </p>
                      <ul className="space-y-3 mb-8">
                        {useCase.benefits.map((benefit, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                            <span className="text-foreground/80 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{benefit}</span>
                          </li>
                        ))}
                      </ul>
                      {/* CTA removed - only in nav and bottom conversion block */}
                    </div>
                    <div>
                      {/* Single border only (no NotchedFrame) */}
                      <div className="rounded-2xl border border-primary/30 bg-card p-8 shadow-lg">
                          {/* Quote */}
                          <blockquote className="text-lg mb-8 leading-[var(--leading-relaxed)] tracking-[var(--tracking-normal)]">
                            "{useCase.quote}"
                          </blockquote>
                          
                          {/* Outcomes */}
                          <div className="grid grid-cols-2 gap-4">
                            {useCase.outcomes.map((outcome, i) => {
                              const OutcomeIcon = outcome.icon;
                              return (
                                <motion.div
                                  key={i}
                                  className="p-4 rounded-xl bg-secondary/50"
                                  initial={{ opacity: 0, y: 10 }}
                                  whileInView={{ opacity: 1, y: 0 }}
                                  viewport={{ once: true }}
                                  transition={{ delay: i * 0.1 }}
                                >
                                  <OutcomeIcon className="w-5 h-5 text-primary mb-2" />
                                  <div className="text-2xl font-bold text-primary mb-1 tracking-[var(--tracking-tighter)]">{outcome.value}</div>
                                  <div className="text-sm text-muted-foreground leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{outcome.label}</div>
                                </motion.div>
                              );
                            })}
                          </div>
                        </div>
                    </div>
                  </Split>
                </ScrollReveal>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <ConversionCTA 
        headline="Ready to Transform Your Sales Process?"
        bullets={[
          'Join teams who\'ve replaced CRM chaos with daily execution clarity',
          'Start your free trial and see the difference',
        ]}
      />
    </MarketingLayout>
  );
}

